import React, { useEffect, useState } from "react";
import { Skeleton } from "../../components/ui/Skeleton";
import { portalApi } from "../../api/portalApi";
import { DOCUMENT_CATEGORY_LABELS, type DocumentCategory } from "../../api/documentsApi";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function categoryLabel(cat: string): string {
  return DOCUMENT_CATEGORY_LABELS[cat as DocumentCategory] ?? cat;
}

const PortalDocumentsPage: React.FC = () => {
  const [rows, setRows] = useState<Awaited<ReturnType<typeof portalApi.documents>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await portalApi.documents();
        setRows(data);
      } catch {
        setError("We couldn’t load your documents. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-navy-900">My documents</h1>
        <p className="mt-1 text-sm text-slate-600">
          Files your credit team has on file for you. You can download copies anytime.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-[#FCD34D] bg-[#FEF3C7] px-4 py-3 text-sm text-[#78350F]">{error}</div>
      )}

      {loading && (
        <div className="space-y-3" aria-busy="true" aria-label="Loading documents">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg bg-slate-200/90" />
          ))}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-600 shadow-sm">
          No documents are available yet. When your advisor uploads credit reports, letters, or other files, they will
          appear here.
        </div>
      )}

      <ul className="space-y-3">
        {!loading &&
          rows.map((d) => (
            <li
              key={d.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-navy-900">{d.fileName}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {categoryLabel(d.category)} · {formatFileSize(d.fileSize)} ·{" "}
                  {new Date(d.createdAt).toLocaleDateString(undefined, { dateStyle: "medium" })}
                  {d.uploadedBy ? ` · From ${d.uploadedBy}` : ""}
                </p>
                {d.description ? <p className="mt-1 text-xs text-slate-600">{d.description}</p> : null}
              </div>
              <button
                type="button"
                onClick={() =>
                  void portalApi.downloadDocument(d.id, d.fileName).catch(() => {
                    setError("Download failed. Please try again.");
                  })
                }
                className="shrink-0 rounded-md border border-navy-200 bg-slate-50 px-3 py-2 text-sm font-medium text-navy-800 hover:bg-slate-100"
              >
                Download
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
};

export default PortalDocumentsPage;
